<script setup lang="ts">
import { type Component, computed } from 'vue';
import { Primitive, type PrimitiveProps } from 'reka-ui';

import { useRootAttrs } from '../composables/useRootAttrs';
import { tv, type VariantProps } from '../tv';
import UiSpinner from './UiSpinner.vue';

defineOptions({
  inheritAttrs: false,
});

// Figma: OMB Kit › Button (Variant × Size × State).
const button = tv({
  slots: {
    base: [
      'relative inline-flex shrink-0 items-center justify-center gap-2.5 rounded-md px-4 font-semibold whitespace-nowrap select-none',
      'transition-[background-color,box-shadow,opacity] duration-200 ease-standard',
      'data-disabled:cursor-not-allowed data-disabled:bg-fill-disabled data-disabled:bg-none data-disabled:text-fg-inverse data-disabled:shadow-none',
    ],
    icon: 'shrink-0',
  },
  variants: {
    variant: {
      gradient: { base: 'bg-brand text-fg hover:shadow-brand-glow' },
      solid: { base: 'bg-fill-inverse text-fg-inverse' },
      soft: { base: 'bg-fill-strong text-fg' },
      glass: { base: 'bg-fill-glass text-fg backdrop-blur-glass' },
      ghost: { base: 'text-fg hover:bg-fill data-disabled:bg-transparent data-disabled:text-fg-subtle' },
      google: { base: 'bg-google text-fg uppercase' },
    },
    size: {
      md: { base: 'h-10 text-button-md', icon: 'size-4' },
      lg: { base: 'h-14.5 text-button-lg', icon: 'size-5' },
    },
    pill: {
      true: { base: 'rounded-full' },
    },
    block: {
      true: { base: 'w-full' },
    },
  },
  compoundVariants: [
    // The Google mark is drawn on its own 22px pad, and the label keeps the
    // 14px of v1's Google button in the 58px height.
    { variant: 'google', size: 'lg', class: { base: 'text-button-md', icon: 'size-5.5' } },
  ],
  defaultVariants: {
    variant: 'gradient',
    size: 'md',
  },
});

type ButtonVariants = VariantProps<typeof button>;

/**
 * A button, or anything rendered as one (`as` a RouterLink, `as="a"`).
 * Icon-only buttons are `UiIconButton`.
 *
 * `gradient` is the call to action (one per screen), `solid` the light
 * alternative, `soft` the quiet one, `glass` sits over images and video,
 * `ghost` is text only, `google` is "Continue with Google" (uppercase, the
 * Google mark passed as its `icon`). `md` (40px) inside content, `lg` (58px) for the main
 * action at the bottom of a screen.
 *
 * `loading` keeps the label in place, swaps the leading icon for a spinner and
 * reports `aria-busy`; the button is inert until it clears. Only `disabled` on a
 * native button uses the `disabled` attribute. A loading button, or any button
 * rendered as something else, is `aria-disabled` and swallows activation
 * instead: a native `disabled` would throw keyboard focus back to <body>
 * mid-submit, and a link ignores the attribute altogether. A button that
 * disables itself when pressed and stays on screen (a resend with a cooldown)
 * passes `focusable-when-disabled` for the same reason. A link is `as="a"`
 * or `as` a RouterLink; there is no `asChild`, since the icon or spinner would
 * render first and take the control's attributes instead of the child.
 *
 * A `class` on it is merged into its own classes, so a conflicting utility
 * (`rounded-full`, `w-auto`) replaces the default instead of racing it.
 */
const props = withDefaults(defineProps<Omit<PrimitiveProps, 'asChild'> & {
  variant?: ButtonVariants['variant'];
  size?: ButtonVariants['size'];
  type?: 'button' | 'submit' | 'reset';
  /** Fully rounded ends. */
  pill?: boolean;
  block?: boolean;
  loading?: boolean;
  disabled?: boolean;
  /** Disabled stays focusable (`aria-disabled`), for a button that disables itself when pressed. */
  focusableWhenDisabled?: boolean;
  icon?: Component;
  trailingIcon?: Component;
}>(), {
  as: 'button',
  variant: 'gradient',
  size: 'md',
  type: 'button',
  icon: undefined,
  trailingIcon: undefined,
});

const { className, attrs } = useRootAttrs();

const isNativeButton = computed(() => props.as === 'button');
const isInert = computed(() => props.disabled || props.loading);
const usesDisabledAttribute = computed(() => isNativeButton.value && props.disabled && !props.focusableWhenDisabled);

const blockWhenInert = (event: Event) => {
  if (isInert.value) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
};

const ui = computed(() => button({
  variant: props.variant,
  size: props.size,
  pill: props.pill,
  block: props.block,
}));
</script>

<template>
  <Primitive
    v-bind="attrs"
    :as="props.as"
    :type="isNativeButton ? props.type : undefined"
    :disabled="usesDisabledAttribute || undefined"
    :aria-disabled="isInert && !usesDisabledAttribute ? 'true' : undefined"
    :aria-busy="props.loading ? 'true' : undefined"
    :data-disabled="props.disabled ? '' : undefined"
    :class="ui.base({ class: className })"
    @click.capture="blockWhenInert"
  >
    <UiSpinner
      v-if="props.loading"
      decorative
      :size="props.size === 'lg' ? 'md' : 'sm'"
    />
    <component
      :is="props.icon"
      v-else-if="props.icon"
      :class="ui.icon()"
      aria-hidden="true"
    />
    <slot />
    <component
      :is="props.trailingIcon"
      v-if="props.trailingIcon"
      :class="ui.icon()"
      aria-hidden="true"
    />
  </Primitive>
</template>
